// Mobile call-forwarding knowledge used by the activation & deactivation flows.
// Codes are carrier-specific: never assume one operator's codes work on another.
import { getDemoDevice } from '../utils/demoDevice'

export type Condition = 'busy' | 'unanswered' | 'unreachable' | 'off' | 'all'
/** User-facing forwarding rules. "unreachable" also covers "phone off" (same carrier rule). */
export type RuleId = 'busy' | 'unanswered' | 'unreachable' | 'all'
export type AnswerMode = 'conditional' | 'all'
export type DeviceOS = 'ios' | 'android'
export type AndroidBrand = 'samsung' | 'other'
export type ActivationMethod = 'code' | 'settings'
export type CarrierId = 'tmobile' | 'att' | 'verizon' | 'etisalat' | 'du' | 'other'

export interface ForwardingSetup {
  carrierId: CarrierId
  os: DeviceOS
  androidBrand: AndroidBrand
  method: ActivationMethod
}

export const VEXU_NUMBER = '+1 (555) 019-8472'
const VEXU_E164 = '+15550198472'
const VEXU_US = '15550198472'

interface CarrierCode {
  activate: string
  deactivate: string
  /** Deactivation removes ALL forwarding on the line, not just this rule. */
  deactivateIsBlanket?: boolean
}

export interface Carrier {
  id: CarrierId
  name: string
  region: string
  /** Validated codes per rule. A missing rule = not validated / not supported. */
  codes: Partial<Record<RuleId, CarrierCode>>
  /** Rules that share one carrier code (dialled once for all of them). */
  sharedCodeNote?: string
  /** iPhone Settings → Phone → Call Forwarding is offered by this carrier. */
  iosSettings: boolean
  /** Android Phone app exposes Always Forward. */
  androidSettingsAll: boolean
  /** Android Phone app exposes busy / unanswered / unreachable individually. */
  androidSettingsConditional: boolean
  /** Only shown when relevant — never a mandatory step. */
  wifiCallingTip?: string
}

export const CARRIERS: Carrier[] = [
  {
    id: 'tmobile',
    name: 'T-Mobile',
    region: 'United States',
    codes: {
      busy: { activate: `**67*${VEXU_US}#`, deactivate: '##67#' },
      unanswered: { activate: `**61*${VEXU_US}#`, deactivate: '##61#' },
      unreachable: { activate: `**62*${VEXU_US}#`, deactivate: '##62#' },
      all: { activate: `**21*${VEXU_US}#`, deactivate: '##21#' },
    },
    iosSettings: true,
    androidSettingsAll: true,
    androidSettingsConditional: true,
  },
  {
    id: 'att',
    name: 'AT&T',
    region: 'United States',
    codes: {
      busy: { activate: `**67*${VEXU_US}#`, deactivate: '##67#' },
      unanswered: { activate: `**61*${VEXU_US}#`, deactivate: '##61#' },
      unreachable: { activate: `**62*${VEXU_US}#`, deactivate: '##62#' },
      all: { activate: `**21*${VEXU_US}#`, deactivate: '##21#' },
    },
    iosSettings: true,
    androidSettingsAll: true,
    androidSettingsConditional: true,
    wifiCallingTip:
      'Forwarding codes can fail while Wi-Fi Calling is on. If the code doesn’t go through, turn off Wi-Fi Calling, dial again, then turn it back on.',
  },
  {
    id: 'verizon',
    name: 'Verizon',
    region: 'United States',
    codes: {
      busy: { activate: `*71${VEXU_US.slice(1)}`, deactivate: '*73', deactivateIsBlanket: true },
      unanswered: { activate: `*71${VEXU_US.slice(1)}`, deactivate: '*73', deactivateIsBlanket: true },
      all: { activate: `*72${VEXU_US.slice(1)}`, deactivate: '*73', deactivateIsBlanket: true },
    },
    sharedCodeNote: 'One code covers both busy and unanswered calls.',
    iosSettings: false,
    androidSettingsAll: false,
    androidSettingsConditional: false,
  },
  {
    id: 'etisalat',
    name: 'e& (Etisalat)',
    region: 'United Arab Emirates',
    codes: {
      busy: { activate: `*67*${VEXU_E164}#`, deactivate: '#67#' },
      unanswered: { activate: `*61*${VEXU_E164}#`, deactivate: '#61#' },
      unreachable: { activate: `*62*${VEXU_E164}#`, deactivate: '#62#' },
      all: { activate: `*21*${VEXU_E164}#`, deactivate: '#21#' },
    },
    iosSettings: true,
    androidSettingsAll: true,
    androidSettingsConditional: true,
  },
  {
    id: 'du',
    name: 'du',
    region: 'United Arab Emirates',
    codes: {
      busy: { activate: `*67*${VEXU_E164}#`, deactivate: '#67#' },
      unanswered: { activate: `*61*${VEXU_E164}#`, deactivate: '#61#' },
      all: { activate: `*21*${VEXU_E164}#`, deactivate: '#21#' },
    },
    iosSettings: true,
    androidSettingsAll: true,
    androidSettingsConditional: true,
    wifiCallingTip:
      'If the code doesn’t go through, turn off Wi-Fi Calling for a moment, dial again, then turn it back on.',
  },
  {
    id: 'other',
    name: 'Another carrier',
    region: 'Not listed',
    codes: {},
    iosSettings: true,
    androidSettingsAll: true,
    androidSettingsConditional: true,
  },
]

export const RULES: {
  id: RuleId
  title: string
  short: string
  desc: string
}[] = [
  {
    id: 'busy',
    title: 'When I’m busy',
    short: 'When busy',
    desc: 'You’re on another call or decline the call.',
  },
  {
    id: 'unanswered',
    title: 'When I don’t answer',
    short: 'When unanswered',
    desc: 'Your phone rings and you don’t pick up.',
  },
  {
    id: 'unreachable',
    title: 'When my phone is unreachable or off',
    short: 'When unreachable or off',
    desc: 'No signal, airplane mode, or powered off.',
  },
  {
    id: 'all',
    title: 'Every call',
    short: 'Every call',
    desc: 'All incoming calls go to Vexu.',
  },
]

export const getCarrier = (id: CarrierId) =>
  CARRIERS.find((c) => c.id === id) ?? CARRIERS[CARRIERS.length - 1]
export const getRule = (id: RuleId) => RULES.find((r) => r.id === id)!

export function detectOS(): DeviceOS {
  const demo = getDemoDevice()
  if (demo) return demo.os
  if (typeof navigator === 'undefined') return 'ios'
  return /android/i.test(navigator.userAgent) ? 'android' : 'ios'
}

/** Carrier lookup from the subscriber's number is a best guess — always confirmed by the user. */
export const DETECTED_CARRIER: CarrierId = 'tmobile'

export function ruleSupportedByCode(carrier: Carrier, rule: RuleId) {
  return Boolean(carrier.codes[rule])
}

/** Is this rule available at all on this carrier + device, via any method? */
export function ruleAvailable(
  carrier: Carrier,
  os: DeviceOS,
  rule: RuleId,
): boolean {
  if (ruleSupportedByCode(carrier, rule)) return true
  if (os === 'android' && rule !== 'all') return carrier.androidSettingsConditional
  if (rule === 'all') return os === 'ios' ? carrier.iosSettings : carrier.androidSettingsAll
  return false
}

export interface MethodAvailability {
  available: boolean
  reason?: string
}

export function methodAvailability(
  method: ActivationMethod,
  ctx: { carrier: Carrier; os: DeviceOS; mode: AnswerMode; rules: RuleId[] },
): MethodAvailability {
  const { carrier, os, mode, rules } = ctx
  if (method === 'code') {
    const needed: RuleId[] = mode === 'all' ? ['all'] : rules
    if (needed.every((r) => ruleSupportedByCode(carrier, r))) return { available: true }
    return {
      available: false,
      reason: 'A verified code for your carrier isn’t available for this setup.',
    }
  }
  if (os === 'ios') {
    if (mode === 'conditional')
      return {
        available: false,
        reason:
          'iPhone Settings forwards every call, so it can’t set up “when I can’t answer”.',
      }
    return carrier.iosSettings
      ? { available: true }
      : { available: false, reason: 'Your carrier doesn’t offer Call Forwarding in iPhone Settings.' }
  }
  if (mode === 'all')
    return carrier.androidSettingsAll
      ? { available: true }
      : { available: false, reason: 'Your carrier doesn’t show call forwarding in the Phone app.' }
  return carrier.androidSettingsConditional
    ? { available: true }
    : {
        available: false,
        reason: 'Your Phone app doesn’t offer separate busy or unanswered forwarding with your carrier.',
      }
}

/** Groups rules that share the same activation code so each code is dialled once. */
export function codeSteps(carrier: Carrier, mode: AnswerMode, rules: RuleId[]) {
  const needed: RuleId[] = mode === 'all' ? ['all'] : rules
  const steps: { code: string; rules: RuleId[] }[] = []
  needed.forEach((r) => {
    const code = carrier.codes[r]?.activate
    if (!code) return
    const existing = steps.find((s) => s.code === code)
    if (existing) existing.rules.push(r)
    else steps.push({ code, rules: [r] })
  })
  return steps
}

/** Rules → the app-wide Condition list (unreachable implies phone-off too). */
export function rulesToConditions(mode: AnswerMode, rules: RuleId[]): Condition[] {
  if (mode === 'all') return ['all']
  return rules.flatMap((r) => (r === 'unreachable' ? ['unreachable', 'off'] : [r])) as Condition[]
}

export function conditionToRule(c: Condition): RuleId {
  return c === 'off' ? 'unreachable' : c
}

export function formatCarrierDisplay(carrier: Carrier) {
  void carrier
  return 'your carrier'
}
